<template>

  <div class="form-group-item">

  <!-- Form group info row -->
  <div class="row">
    <div class="col-md-11 col-sm-10 col-xs-10" :style="nestingIndentStyle">
      <a @click="toggleContent">

        <!-- Select group forms checkbox -->
        <div class="float-left ml-3 mt-2">
          <i v-if="isContentLoading && !isEmpty" class="fa fa-spinner fa-spin fa-2x"></i>
          <template v-else>
            <input :name="'select-all-' + getGroupKey(group)" class="custom-checkbox" :checked="contentSelected"
                   type="checkbox" :disabled="isEmpty">
            <label :for="'select-all-' + getGroupKey(group)" @click.stop="toggleSelect"></label>
          </template>
        </div>

        <!-- Expand/collapse state icon -->
        <div class="form-group-icon">
          <i :class="'fa fa-angle' + (contentDisplayed ? '-down' : '-right')"></i>
        </div>

        <!-- Form group name and stats -->
        <div class="form-group-name">

          <!-- Form group "zip" icon -->
          <i v-if="isZipArchive" class="fa fa-file-archive-o"></i>
          <!--<i v-else :class="'fa fa-folder' + (contentDisplayed ? '-open' : '')"></i>-->

          <!-- Form group name (muted if group is inactive) -->
          <span v-if="!group.status">
            <s class="text-muted">{{ group.name }}</s>
          </span>
          <span v-else>
            {{ group.name }}
          </span>

          <!-- Form group stats if group is inactive: all positive numbers shown muted -->
          <template v-if="!group.status">
            <template v-if="groupCount">
              <span class="badge badge-muted"><i class="fa fa-folder"></i> {{ groupCount }}</span>
            </template>
            <template v-if="formCount">
              <span v-if="formCount" class="badge badge-muted">{{ formCount }}</span>
            </template>
          </template>

          <!-- Form group stats if group is active: active sub-items shown green, inactive shown red -->
          <template v-else>
            <template v-if="groupCount">
              <span v-if="activeGroupCount" class="badge badge-info"><i class="fa fa-folder"></i> {{ activeGroupCount }}</span>
              <span v-if="showAllGroupsMode && inactiveGroupCount" class="badge badge-danger"><i class="fa fa-folder"></i> {{ inactiveGroupCount }}</span>
            </template>
            <template v-if="formCount">
              <span v-if="activeFormCount" class="badge badge-info">{{ activeFormCount }}</span>
              <span v-if="showAllGroupsMode && inactiveFormCount" class="badge badge-danger">{{ inactiveFormCount }}</span>
            </template>
          </template>

        </div>
      </a>
    </div>

    <!-- Per-group actions -->
    <div class="col-md-1 col-sm-2 col-xs-2" v-if="!viewMode">
      <div class="form-group-name">
        <template v-if="!group.is_virtual">
          <a v-if="manageGroupsAllowed" :href="group.edit_link" class="btn btn-sm btn-primary"><i class="fa fa-fw fa-pencil"></i> Edit</a>
        </template>
        <template v-else>
          <a v-if="isZipArchive" :href="group.download_link" class="btn btn-sm btn-info"><i class="fa fa-download"></i> Zip</a>
        </template>
      </div>
    </div>
  </div>
  <!-- // End of form group info row -->

  <!-- Collapsible group content: forms, subgroups -->
  <form-group-content
      :parent-group="group"
      :tab="tab"
      :show-all-groups-mode="showAllGroupsMode"
      :manage-groups-allowed="manageGroupsAllowed"
      :no-forms-message="noFormsMessage"
      :do-display-content="doDisplayContent"
      :do-load-content="doLoadContent"
      :do-select-content="doSelectContent"
      :nesting-level="nestingLevel"
      :view-mode="viewMode"
      @updateIsEmpty="updateIsEmpty($event)"
      @onContentLoad="contentLoaded = $event"
      @onContentIsLoading="isContentLoading = $event"
      @onContentSelect="onContentSelect($event)"
      @selectedFormsUpdated="updateSelectedForms($event)"
      @selectedGroupsUpdated="updateSelectedGroups($event)"
  >
  </form-group-content>
  <!-- // End of collapsible group content: forms, subgroups -->

  </div>

</template>

<script>
import FormGroupContent from "./FormGroupContent";

/**
 * This widget is a part of FormExplorer. It shows a single form group.
 */
export default {

  emits: [
    'groupSelectionChanged',
    'selectedFormsUpdated',
    'selectedGroupsUpdated',
    'updateIsEmpty',
    'onContentIsLoading'
  ],

  /**
   * Input properties.
   *s
   * Usage examples:
   *
   <form-group
     :group="group"
     :tab="tab"
     :show-all-groups-mode="showAllGroupsMode"
     :manage-groups-allowed="manageGroupsAllowed"
     :no-forms-message="noFormsMessage"
   >
   </form-group>
   */
  props: {

    // Group to render.
    group: {
      type: Object,
      default: () => {return {};}
    },

    // Current tab key.
    tab: {
      type: String,
      default: 'active'
    },

    // The message to be shown if no data loaded.
    noFormsMessage: {
      type: String,
      default: 'No forms'
    },

    // Show all groups mode, including inactive.
    showAllGroupsMode: {
      type: Boolean,
      default: false
    },

    // Indicates if user can manage the groups.
    manageGroupsAllowed: {
      type: Boolean,
      default: false
    },

    // Current group nesting level.
    nestingLevel: {
      type: Number,
      default: 0
    },

    // Show widget in "view mode" (no "edit" buttons, etc).
    viewMode: {
      type: Boolean,
      default: false
    },

    // Show groups expanded by default.
    expanded: {
      type: Boolean,
      default: false
    },

  },

  /**
   * Variables (models).
   */
  data() {
    return {
      // Load group content action.
      doLoadContent: false,
      // Load group content action result: is content loaded.
      contentLoaded: false,
      // Is content currently loading indicator.
      isContentLoading: false,

      // Display group content action.
      doDisplayContent: false,
      // Display group content action result: is content displayed.
      contentDisplayed: false,

      // Select all group items.
      doSelectContent: -1,
      // Is all group items selected.
      contentSelected: false,

      // Selected form ids in current group and all sub-groups.
      selectedFormIds: [],
      // Selected group keys in current group and all sub-groups.
      selectedGroupKeys: [],

      // Is group empty.
      isEmpty: false,
    }
  },

  /**
   * Functions (methods).
   */
  methods: {

    /**
     * Loads group content.
     */
    loadContent() {
      if (!this.contentLoaded) {
        this.doLoadContent = true;
      }
    },

    /**
     * Opens (expand) group content.
     */
    toggleContent() {
      this.loadContent();
      // This triggers FormGroupContent component to show/hide the group content.
      this.doDisplayContent = !this.contentDisplayed;
      // This updates state "contentDisplayed" variable for current component.
      this.contentDisplayed = this.doDisplayContent;
    },

    /**
     * Toggle select all group items.
     */
    toggleSelect() {
      this.select(!this.contentSelected);
    },

    /**
     * Select/unselect all group items.
     */
    select(forceSelect) {
      // This triggers FormGroupContent component to select/deselect the group items.
      this.doSelectContent = forceSelect ?
        // Using increment/decrement positive/negative numbers to fire children component watcher correctly.
        ((this.doSelectContent < 0 ? this.doSelectContent * -1 : this.doSelectContent) + 1) :
        ((this.doSelectContent > 0 ? this.doSelectContent * -1 : this.doSelectContent) - 1);
    },

    /**
     * Event fired when content selection changed.
     */
    onContentSelect(isSelected) {
      this.contentSelected = isSelected;
      this.$emit('groupSelectionChanged', isSelected);
    },

    /**
     * Update selected form ids from form-group-content sub-component.
     */
    updateSelectedForms(newFormIds) {
      this.selectedFormIds = newFormIds;
      this.$emit('selectedFormsUpdated', this.selectedFormIds);
    },

    /**
     * Update selected group keys from form-group-content sub-component.
     */
    updateSelectedGroups(newGroupKeys) {
      this.selectedGroupKeys = newGroupKeys;
      this.$emit('selectedGroupsUpdated', this.selectedGroupKeys);
    },

    /**
     * Generate a group key.
     */
    getGroupKey(group) {
      return group.id + (group.zip_item ? '_' + group.zip_item.index : '');
    },

    /**
     * Updates isEmpty status from child FromGroupContent component and send updated status to parent FromGroupContent.
     */
    updateIsEmpty(isEmpty) {
      this.isEmpty = isEmpty;
      this.$emit('updateIsEmpty', this.isEmpty);
    },

  },

  /**
   * Computed values.
   */
  computed: {

    /**
     * Generates CSS style for element indent by nesting level.
     */
    nestingIndentStyle() {
      let level = this.nestingLevel;
      if (level > 12) level = 12;
      return 'padding-left: '+ (15 + level * 30) +'px;';
    },

    /**
     * Is current group actually a zip archive.
     */
    isZipArchive() {
      return this.group.is_virtual && this.group.virtual_type === 'zip';
    },

    /**
     * Amount of forms in a group.
     */
    formCount() {
      return this.group.forms_count - this.group.active_zips_count - this.group.inactive_zips_count;
    },

    /**
     * Amount of active forms in a group.
     */
    activeFormCount() {
      return this.group.active_forms_count - this.group.active_zips_count;
    },

    /**
     * Amount of inactive forms in a group.
     */
    inactiveFormCount() {
      return this.group.inactive_forms_count - this.group.inactive_zips_count;
    },

    /**
     * Amount of subgroups in a group.
     */
    groupCount() {
      return this.group.children_count + this.group.active_zips_count + this.group.inactive_zips_count;
    },

    /**
     * Amount of active subgroups in a group.
     */
    activeGroupCount() {
      return this.group.active_children_count + this.group.active_zips_count;
    },

    /**
     * Amount of inactive subgroups in a group.
     */
    inactiveGroupCount() {
      return this.group.inactive_children_count + this.group.inactive_zips_count;
    },

  },

  /**
   * Component initialization.
   */
  mounted: function () {
    if (this.expanded) this.toggleContent();
    if (!this.formCount && !this.groupCount) this.isEmpty = true;
  },

  /**
   * Watched values.
   */
  watch : {

    /**
     * Fire event to update parent.
     */
    isContentLoading: function(val) {
      this.$emit('onContentIsLoading', val);
    },

  },

  /**
   * Used components.
   */
  components: {
    FormGroupContent
  },
}
</script>
